import { t as globalT, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Anchor,
  Badge,
  Button,
  Group,
  Stack,
  TagsInput,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useUpdateMutation } from "@supabase-cache-helpers/postgrest-react-query";
import { ErrorAlert, LoadingSuspense } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useCallback } from "react";

import { api } from "@/api.ts";
import { ProjectSelect } from "@/components/ProjectSelect.tsx";
import { DatasetUriMultiSelect } from "@/components/clients/DatasetUriMultiSelect.tsx";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";
import { useZitadelApplications } from "@/hooks/useZitadelApplications.ts";

export const Route = createFileRoute("/admin/clients/$id")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Edit client`,
      },
    ],
  }),
});

const EditForm: FC<{ id: string }> = ({ id }) => {
  const { data } = useSuspenseQueryPostgrest(
    api
      .from("client")
      .select("*,id::text")
      .eq("id", id as unknown as number)
      .single(),
  );
  const { forClientId } = useZitadelApplications();
  const application = forClientId(id);

  const form = useForm({
    initialValues: {
      name: data.name,
      owned_by: data.owned_by ?? "",
      redirect_uris: data.redirect_uris,
      client_datasets: data.client_datasets,
      permitted_datasets: data.permitted_datasets,
      project_id: data.project_id,
      client_dataset_project_id: data.client_dataset_project_id,
    },
    validate: {
      name: (value) => (value.trim().length > 0 ? null : t`Name is required`),
    },
  });

  const { mutateAsync: update } = useUpdateMutation(
    api.from("client"),
    ["id"],
    "id",
  );

  const handleSubmit = useCallback<Parameters<(typeof form)["onSubmit"]>[0]>(
    async (values) => {
      try {
        await update({
          id: id as unknown as number,
          name: values.name,
          owned_by: values.owned_by || null,
          redirect_uris: values.redirect_uris,
          client_datasets: values.client_datasets,
          permitted_datasets: values.permitted_datasets,
          project_id: values.project_id,
          client_dataset_project_id: values.client_dataset_project_id,
        });
      } catch (e) {
        if (e instanceof Error) {
          form.setFieldError("__root", e.message);
        }
      }
    },
    [form, update, id],
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <Group justify="space-between" align="center">
          <h1>
            <Trans>Edit client</Trans>
          </h1>
          {application ? (
            <Anchor
              href={application.console_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Trans>View in Zitadel</Trans>
            </Anchor>
          ) : (
            <Tooltip
              label={
                <Trans>
                  No matching application found in Zitadel for this client ID
                </Trans>
              }
            >
              <Badge color="gray" variant="light">
                <Trans>Not found in Zitadel</Trans>
              </Badge>
            </Tooltip>
          )}
        </Group>
        <TextInput label={t`Client ID`} value={id} readOnly disabled />
        <TextInput
          label={t`Name`}
          withAsterisk
          disabled={form.submitting}
          key={form.key("name")}
          {...form.getInputProps("name")}
        />
        <TextInput
          label={t`Owned by`}
          description={t`Email address of the user responsible for this client`}
          disabled={form.submitting}
          key={form.key("owned_by")}
          {...form.getInputProps("owned_by")}
        />
        <TagsInput
          label={t`Redirect URIs`}
          disabled={form.submitting}
          key={form.key("redirect_uris")}
          {...form.getInputProps("redirect_uris")}
        />
        <DatasetUriMultiSelect
          label={t`Client datasets`}
          description={t`Datasets this client has implicit access to, without specifying a project`}
          disabled={form.submitting}
          value={form.values.client_datasets}
          onChange={(value) => form.setFieldValue("client_datasets", value)}
        />
        <DatasetUriMultiSelect
          label={t`Permitted datasets`}
          description={t`Datasets this client has access to without project-level permission`}
          disabled={form.submitting}
          value={form.values.permitted_datasets}
          onChange={(value) => form.setFieldValue("permitted_datasets", value)}
        />
        <ProjectSelect
          label={t`Project`}
          description={t`The project this client belongs to, if any`}
          clearable
          disabled={form.submitting}
          value={form.values.project_id}
          onChange={(value) => form.setFieldValue("project_id", value)}
        />
        <ProjectSelect
          label={t`Client dataset project`}
          description={t`Project to use if no other project is specified and only datasets within "Client datasets" are accessed`}
          clearable
          disabled={form.submitting}
          value={form.values.client_dataset_project_id}
          onChange={(value) =>
            form.setFieldValue("client_dataset_project_id", value)
          }
        />
        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            loading={form.submitting}
            disabled={form.submitting}
          >
            <Trans>Save</Trans>
          </Button>
        </Group>
        {form.errors.__root && <ErrorAlert>{form.errors.__root}</ErrorAlert>}
      </Stack>
    </form>
  );
};

function RouteComponent() {
  const { id } = Route.useParams();

  return (
    <LoadingSuspense>
      <EditForm id={id} />
    </LoadingSuspense>
  );
}
